import { MutationObserverOptions, QueryKey, useMutation, useQueryClient } from '@tanstack/vue-query'
import { toast } from './components/ui/toast'
import { TypeFromFormatter } from './stores/config'
import { usePejerBase } from './stores/pejerData'
import { ApiUrls } from './types/base'
import { ExtractParams } from './types/HelperTypes'
import { DataState, ErrorObject } from './types/types'

export default function usePejerMutation<T extends ApiUrls<L, any>, L>(
  apiUrl: T,
  options?: MutationObserverOptions<
    TypeFromFormatter<T>,
    ErrorObject,
    {
      pathArgs?: Record<ExtractParams<T['path']>, string | number>
      body?: any
    }
  > & {
    queryKeys?: QueryKey[]
  },
  extraOptions?: {
    ignoreAuthorization?: boolean
    toastOnSuccess?: boolean | Parameters<typeof toast>[0]
  }
) {
  const pejer = usePejerBase()
  const queryClient = useQueryClient()
  const { queryKeys: _, ...mutationOptions } = options || {}
  const mutation = useMutation({
    ...mutationOptions,
    mutationFn: async (options) => {
      const res = await pejer.fetch<T, TypeFromFormatter<T>>(apiUrl, {
        ...options,
        ...extraOptions,
      })
      if ('error' in res) throw res
      return res.data
    },
    onError: (e) => {
      toast({
        title: 'Action failed',
        description: e.error,
        variant: 'destructive',
      })
      console.error(e)
    },
    onSuccess: (data, variables, context) => {
      if (options?.queryKeys)
        for (const key of options.queryKeys)
          queryClient.invalidateQueries({
            queryKey: key,
          })

      if (extraOptions?.toastOnSuccess ){
        if (typeof extraOptions.toastOnSuccess === 'object')
          toast(extraOptions?.toastOnSuccess )
        else 
          toast( {
            title: 'Action successful',
            description: 'The action was completed successfully',
            variant: 'default',
          } )
      }
      options?.onSuccess?.(data, variables, context)
    },
  })

  return {
    ...mutation,
    dataState: {
      isLoading: mutation.isPending,
      isSuccess: mutation.isSuccess,
      isError: mutation.isError,
      error: mutation.error,
    } as DataState,
  }
}
